/**
 * Vue Router Configuration
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { authGuard } from '@/guards/auth.guard'
import { roleGuard } from '@/guards/role.guard'
import { UserRole } from '@/types/auth.types'
import { useEasyNav } from '@/composables/useEasyNav'
import { useAuthStore } from '@/stores/auth.store'

// Layouts
import MainLayout from '@/layouts/MainLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'

// Views - Lazy loaded
const LoginView = () => import('@/views/auth/LoginView.vue')
const RegisterView = () => import('@/views/auth/RegisterView.vue')
const OnboardingView = () => import('@/views/auth/OnboardingView.vue')
const AuthCallbackView = () => import('@/views/auth/AuthCallbackView.vue')
const ForgotPasswordView = () => import('@/views/auth/ForgotPasswordView.vue')
const ResetPasswordView = () => import('@/views/auth/ResetPasswordView.vue')

const HomeView = () => import('@/views/HomeView.vue')
const ChatView = () => import('@/views/ChatView.vue')
const ProfileView = () => import('@/views/ProfileView.vue')
const EventsView = () => import('@/views/EventsView.vue')
const EducationView = () => import('@/views/EducationView.vue')
const EarnView = () => import('@/views/EarnView.vue')
const ConnectView = () => import('@/views/ConnectView.vue')
const ChaptersView = () => import('@/views/ChaptersView.vue')
const GuideView = () => import('@/views/GuideView.vue')
const FeedbackView = () => import('@/views/FeedbackView.vue')
const ManageSubmissionsView = () => import('@/views/ManageSubmissionsView.vue')
const PrivacyView = () => import('@/views/PrivacyView.vue')
const TermsView = () => import('@/views/TermsView.vue')
const VideoSummaryView = () => import('@/views/VideoSummaryView.vue')
const OAuthAuthorizeView = () => import('@/views/OAuthAuthorizeView.vue')

// EasyNav — Guided Onboarding
const EasyNavView = () => import('@/views/EasyNavView.vue')
const EasyNavPathView = () => import('@/views/EasyNavPathView.vue')
const EasyNavPartnerView = () => import('@/views/EasyNavPartnerView.vue')
const OrganizeView = () => import('@/views/OrganizeView.vue')
const EngageView = () => import('@/views/EngageView.vue')
const EasyNavConnectView = () => import('@/views/easynav/EasyNavConnectView.vue')
const EasyNavOrganizeView = () => import('@/views/easynav/EasyNavOrganizeView.vue')
const EasyNavVenueView = () => import('@/views/easynav/EasyNavVenueView.vue')
const EasyNavSpeakerView = () => import('@/views/easynav/EasyNavSpeakerView.vue')
const EasyNavFundingView = () => import('@/views/easynav/EasyNavFundingView.vue')
const EasyNavSponsorView = () => import('@/views/easynav/EasyNavSponsorView.vue')
const EasyNavPartnerConnectView = () => import('@/views/EasyNavPartnerConnectView.vue')

// Admin Views
const AdminDashboardView = () => import('@/views/admin/AdminDashboardView.vue')

const routes: RouteRecordRaw[] = [
    // Auth routes (guest only)
    {
        path: '/auth',
        component: AuthLayout,
        meta: { requiresGuest: true },
        children: [
            {
                path: 'login',
                name: 'login',
                component: LoginView,
                meta: { title: 'Sign In' },
            },
            {
                path: 'register',
                name: 'register',
                component: RegisterView,
                meta: { title: 'Create Account' },
            },
            {
                path: 'forgot-password',
                name: 'forgot-password',
                component: ForgotPasswordView,
                meta: { title: 'Reset Password' },
            },
        ],
    },

    // Auth callback — NOT guest-only (user becomes authenticated during this flow)
    {
        path: '/auth/callback',
        name: 'auth-callback',
        component: AuthCallbackView,
        meta: { title: 'Authenticating...' },
    },

    // Reset password — NOT guest-only (user is authenticated by the magic link before loading)
    {
        path: '/auth/reset-password',
        component: AuthLayout,
        children: [
            {
                path: '',
                name: 'reset-password',
                component: ResetPasswordView,
                meta: { title: 'Update Password' },
            }
        ]
    },

    // Onboarding (authenticated but not completed onboarding)
    {
        path: '/onboarding',
        name: 'onboarding',
        component: OnboardingView,
        meta: { requiresAuth: true, title: 'Complete Your Profile' },
    },

    // OAuth 2.0 PKCE Authorization Consent Screen (Claude, OpenAI, Dots, AI Agents)
    {
        path: '/oauth/authorize',
        name: 'oauth-authorize',
        component: OAuthAuthorizeView,
        meta: { requiresAuth: true, title: 'Authorize AI Assistant' },
    },

    // Main app routes (authenticated)
    {
        path: '/',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                name: 'home',
                component: HomeView,
                meta: { title: 'Dashboard' },
            },
            {
                path: 'chat/:id?',
                name: 'chat',
                component: ChatView,
                meta: { title: 'Assistant' },
            },
            {
                path: 'profile',
                name: 'profile',
                component: ProfileView,
                meta: { title: 'My Profile' },
            },
            {
                path: 'profile/:id',
                name: 'member-profile',
                redirect: to => {
                    return { path: '/chapters', query: { profileId: to.params.id } }
                }
            },
            {
                path: 'events',
                name: 'events',
                component: EventsView,
                meta: { title: 'Events' },
                beforeEnter: (_to, _from, next) => {
                    const easyNav = useEasyNav()
                    const authStore = useAuthStore()
                    if (easyNav.isEnabled.value && easyNav.persona.value === 'organizer' && !authStore.userRoles.includes(UserRole.CHAPTER_LEAD)) {
                        next({ name: 'easy-nav-organize' })
                    } else {
                        next()
                    }
                }
            },
            {
                path: 'organize',
                name: 'organize',
                component: OrganizeView,
                meta: { title: 'Organize' },
            },
            {
                path: 'education',
                name: 'education',
                component: EducationView,
                meta: { title: 'Education' },
            },
            {
                path: 'earn',
                name: 'earn',
                component: EarnView,
                meta: { title: 'Earn' },
            },
            {
                path: 'connect',
                name: 'connect',
                component: ConnectView,
                meta: { title: 'Connect' },
            },
            {
                path: 'chapters',
                name: 'chapters',
                component: ChaptersView,
                meta: { title: 'Chapters' },
            },
            {
                path: 'guide',
                name: 'guide',
                component: GuideView,
                meta: { title: 'User Guide' },
            },
            {
                path: 'feedback',
                name: 'feedback',
                component: FeedbackView,
                meta: { title: 'Feedback & Support' },
            },
            {
                path: 'privacy',
                name: 'privacy',
                component: PrivacyView,
                meta: { title: 'Privacy Policy', public: true },
            },
            {
                path: 'terms',
                name: 'terms',
                component: TermsView,
                meta: { title: 'Terms & Conditions', public: true },
            },
            {
                path: 'video/summary',
                name: 'video-summary',
                component: VideoSummaryView,
                meta: { title: 'Video Summary' },
            },
            // ── EasyNav — Guided Onboarding ─────────────────────────────────────
            {
                path: 'easy-nav',
                name: 'easy-nav',
                component: EasyNavView,
                meta: { title: 'Get Started' },
            },
            {
                // Partner-specific form must come BEFORE the generic :persona route
                path: 'easy-nav/partner-form',
                name: 'easy-nav-partner-form',
                component: EasyNavPartnerView,
                meta: { title: 'Partner & Sponsor' },
            },
            {
                path: 'easy-nav/partner/connect',
                name: 'easy-nav-partner-connect',
                component: EasyNavPartnerConnectView,
                meta: { title: 'Partner Connect' },
            },
            {
                path: 'easy-nav/connect',
                name: 'easy-nav-connect',
                component: EasyNavConnectView,
                meta: { title: 'Make a Connection' },
            },
            {
                path: 'easy-nav/connect/smart-search',
                name: 'easy-nav-connect-smart-search',
                component: ConnectView,
                meta: { title: 'Smart Search', easyMode: 'smart-search' },
            },
            {
                path: 'easy-nav/connect/directory',
                name: 'easy-nav-connect-directory',
                component: ConnectView,
                meta: { title: 'Member Directory', easyMode: 'directory' },
            },
            {
                path: 'easy-nav/connect/engage',
                name: 'easy-nav-connect-engage',
                component: EngageView,
                meta: { title: 'Engage Members', easyMode: 'engage' },
            },
            {
                path: 'easy-nav/connect/similar-interests',
                name: 'easy-nav-connect-similar',
                component: ConnectView,
                meta: { title: 'Find by Similar Interests', easyMode: 'similar-interests' },
            },
            {
                path: 'easy-nav/connect/selecting-interests',
                name: 'easy-nav-connect-selecting',
                component: ConnectView,
                meta: { title: 'Find by Selecting Interests & Skills', easyMode: 'selecting-interests' },
            },
            {
                path: 'easy-nav/connect/attendees',
                name: 'easy-nav-connect-attendees',
                component: ConnectView,
                meta: { title: 'Connect with Event Attendees', easyMode: 'attendees' },
            },
            {
                path: 'easy-nav/connect/groups',
                name: 'easy-nav-connect-groups',
                component: ConnectView,
                meta: { title: 'Browse Groups', easyMode: 'groups' },
            },
            {
                path: 'easy-nav/connect/invite',
                name: 'easy-nav-connect-invite',
                component: ConnectView,
                meta: { title: 'Invite a New User', easyMode: 'invite' },
            },
            {
                path: 'easy-nav/connect/connections',
                name: 'easy-nav-connect-connections',
                component: ConnectView,
                meta: { title: 'My Connections', easyMode: 'connections' },
            },
            {
                path: 'easy-nav/connect/imports',
                name: 'easy-nav-connect-imports',
                component: ConnectView,
                meta: { title: 'Import', easyMode: 'imports' },
            },
            {
                path: 'easy-nav/organize',
                name: 'easy-nav-organize',
                component: EasyNavOrganizeView,
                meta: { title: 'Organize a Meetup' },
            },
            {
                path: 'easy-nav/organize/start-chapter',
                name: 'easy-nav-organize-start',
                component: OrganizeView,
                meta: { title: 'Start a Chapter', easyMode: 'start-chapter' },
            },
            {
                path: 'easy-nav/organize/venue',
                name: 'easy-nav-organize-venue',
                component: EasyNavVenueView,
                meta: { title: 'Find a Venue' },
            },
            {
                path: 'easy-nav/organize/speaker',
                name: 'easy-nav-organize-speaker',
                component: EasyNavSpeakerView,
                meta: { title: 'Find a Speaker' },
            },
            {
                path: 'easy-nav/funding',
                name: 'easy-nav-funding',
                component: EasyNavFundingView,
                meta: { title: 'Funding Options' },
            },
            {
                path: 'easy-nav/funding/sponsor',
                name: 'easy-nav-organize-sponsor',
                component: EasyNavSponsorView,
                meta: { title: 'Find a Sponsor' },
            },
            {
                path: 'easy-nav/:persona',
                name: 'easy-nav-path',
                component: EasyNavPathView,
                meta: { title: 'Your Path' },
            },
        ],
    },

    // Curator/Admin routes
    {
        path: '/submissions',
        component: MainLayout,
        meta: { requiresAuth: true },
        beforeEnter: roleGuard(UserRole.CURATOR),
        children: [
            {
                path: '',
                name: 'submissions',
                component: ManageSubmissionsView,
                meta: { title: 'Manage Submissions' },
            },
        ],
    },

    // Admin routes
    {
        path: '/admin',
        component: MainLayout,
        meta: { requiresAuth: true },
        beforeEnter: roleGuard(UserRole.ADMINISTRATOR),
        children: [
            {
                path: '',
                alias: ['community-manager'],
                name: 'admin',
                component: AdminDashboardView,
                meta: { title: 'Admin Dashboard' },
            },
        ],
    },

    // Catch-all redirect
    {
        path: '/:pathMatch(.*)*',
        redirect: '/',
    },
]

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(_to, _from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        }
        return { top: 0 }
    },
})

// Global navigation guard
router.beforeEach(authGuard)

// Update document title and track last visited route
router.afterEach((to) => {
    const appName = 'Agent MonDAI'
    const pageTitle = to.meta.title as string | undefined
    document.title = pageTitle ? `${pageTitle} | ${appName}` : appName

    // Track last visited route for EasyNav mode (skip auth and onboarding)
    if (
        !to.path.startsWith('/auth') &&
        !to.path.startsWith('/onboarding')
    ) {
        const authStore = useAuthStore()
        if (authStore.isAuthenticated && authStore.easyNavPrefs.enabled) {
            localStorage.setItem('mondai-last-visited-url', to.fullPath)
        }
    }
})

export default router
